// eslint-disable-next-line no-restricted-imports
import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import { positiveMod } from '@djs';

export interface Format {
  /**
   * Возвращает форматированное число
   * @param {number} value число
   */
  number(value: number): string;

  /**
   * Возвращает название дня недели
   * @param {number} dayNumber номер дня недели (начиная с нуля воскресенья)
   * @param {boolean} options.short в краткой форме
   */
  weekDay(dayNumber: number, options?: { short?: boolean }): string;

  /**
   * Возвращает название месяца
   * @param {number} monthNumber номер месяца (начиная с нуля января)
   * @param {boolean} options.of в родительном падеже
   * @param {boolean} options.short в краткой форме
   */
  month(monthNumber: number, options?: { of?: boolean; short?: boolean }): string;

  /**
   * Формат времени (12- или 24-часовой)
   */
  timeFormat: '12hour' | '24hour';

  /**
   * Возвращает форматированное время
   * @param {string | number | Date} datetime время в формате ISO8601, Unix или объект Date
   */
  time(datetime: string | number | Date): string;

  /**
   * Возвращает форматированную дату (число с месяцем, а также год, если отличается от текущего)
   * @param {string | number | Date} datetime время в формате ISO8601, Unix или объект Date
   * @param {boolean} options.relative допускается относительный формат (вчера, сегодня, завтра)
   * @param {boolean} options.shortMonth название месяца в краткой форме
   * @param {boolean} options.thisYear указываь год даже если он совпадает с текущим
   */
  date(
    datetime: string | number | Date,
    options?: { relative?: boolean; shortMonth?: boolean; thisYear?: boolean },
  ): string;

  /**
   * Возвращает форматированную дату (число с месяцем, а также год, если отличается от текущего) со временем
   * @param {string | number | Date} datetime время в формате ISO8601, Unix или объект Date
   * @param {boolean} options.relative допускается относительный формат (вчера, сегодня, завтра)
   * @param {boolean} options.shortMonth название месяца в краткой форме
   * @param {boolean} options.thisYear указываь год даже если он совпадает с текущим
   */
  dateTime(
    datetime: string | number | Date,
    options?: { relative?: boolean; shortMonth?: boolean; thisYear?: boolean },
  ): string;

  /**
   * Возвращает время в относительной форме от текущего момента (минуту назад, два часа назад, и т.д.)
   * Если просто более трех часов, то указывается дата со временем, если год не совпадает - дата с годом.
   * @param {string | number | Date} datetime время в формате ISO8601, Unix или объект Date
   */
  timeAgo(datetime: string | number | Date): string;
}

export const initFormat = (t: TFunction): Format => {
  const MINUTE = 60;
  const HOUR = 60 * MINUTE;

  const [, oneMinAgo, twoMinAgo, threeMinAgo, fourMinAgo, fiveMinAgo] = t('format:wordMinsAgo', {
    returnObjects: true,
  }) as string[];

  const [, oneHourAgo, twoHoursAgo, threeHoursAgo] = t('format:wordHoursAgo', {
    returnObjects: true,
  }) as string[];

  const timeAgoRanges = [
    { to: 0.75 * MINUTE, template: () => t('format:justNow') },
    { to: MINUTE + 0.5, template: () => oneMinAgo },
    { to: 2 * MINUTE + 0.5, template: () => twoMinAgo },
    { to: 3 * MINUTE + 0.5, template: () => threeMinAgo },
    { to: 4 * MINUTE + 0.5, template: () => fourMinAgo },
    { to: 5 * MINUTE + 0.5, template: () => fiveMinAgo },
    { to: 50 * MINUTE, template: (seconds: number) => t('format:minsAgo', { count: Math.round(seconds / MINUTE) }) },
    { to: HOUR + 0.5, template: () => oneHourAgo },
    { to: 2 * HOUR + 0.5, template: () => twoHoursAgo },
    { to: 3 * HOUR + 0.5, template: () => threeHoursAgo },
  ];

  const values = {
    numDec: t('format:numDec'),
    numDelS: t('format:numDelS'),
    timeSys: t('format:timeSys', { returnObjects: true }) as string[],
    weekDays: t('format:weekDays', { returnObjects: true }) as string[],
    shortWeekDays: t('format:weekDaysShort', { returnObjects: true }) as string[],
    month: t('format:months', { returnObjects: true }) as string[],
    monthsOf: t('format:monthsOf', { returnObjects: true }) as string[],
    shortMonthsOf: t('format:monthsShortOf', { returnObjects: true }) as string[],
  };

  return {
    number(value) {
      const parts = String(value).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, values.numDelS);

      return parts.join(values.numDec);
    },
    weekDay(dayNumber, options = {}) {
      return (options.short ? values.shortWeekDays : values.weekDays)[positiveMod(dayNumber - 1, 7)];
    },
    month(monthNumber, options = {}) {
      return (options.short ? values.shortMonthsOf : options.of ? values.monthsOf : values.month)[
        positiveMod(monthNumber, 12)
      ];
    },
    get timeFormat() {
      return values.timeSys[0] as any;
    },
    time(datetime) {
      return dayjs(datetime).format(this.timeFormat === '12hour' ? 'h:mm a' : 'HH:mm');
    },
    date(datetime, options = {}) {
      const targetDatetime = dayjs(datetime);
      const withYear = options.thisYear || targetDatetime.year() !== dayjs().year();

      const [, date, yesterday, today, tomorrow] = t(withYear ? 'format:date' : 'format:shortDate', {
        returnObjects: true,
        month: this.month(targetDatetime.month(), { short: options.shortMonth, of: true }),
        day: targetDatetime.date(),
        year: targetDatetime.year(),
      }) as string[];

      if (!options.relative) {
        return date;
      }

      const dayDiff = targetDatetime.startOf('day').diff(dayjs().startOf('day'), 'day');

      switch (dayDiff) {
        case -1:
          return yesterday;
        case 0:
          return today;
        case 1:
          return tomorrow;
        default:
          return date;
      }
    },
    dateTime(datetime, options = {}) {
      const targetDatetime = dayjs(datetime);
      const withYear = options.thisYear || targetDatetime.year() !== dayjs().year();

      const tOptions = {
        returnObjects: true,
        day: targetDatetime.date(),
        month: this.month(targetDatetime.month(), { short: options.shortMonth, of: true }),
        year: targetDatetime.year(),
        hour: targetDatetime.format(this.timeFormat === '12hour' ? 'h' : 'HH'),
        minute: targetDatetime.format('mm'),
        am_pm: targetDatetime.format('a'),
      };

      const [, date] = t(withYear ? 'format:dateYearTime' : 'format:shortDateTime', tOptions) as string[];
      const [, , yesterday, today, tomorrow] = t('format:shortDateTime', tOptions) as string[];

      if (!options.relative) {
        return date;
      }

      const dayDiff = targetDatetime.startOf('day').diff(dayjs().startOf('day'), 'day');

      switch (dayDiff) {
        case -1:
          return yesterday;
        case 0:
          return today;
        case 1:
          return tomorrow;
        default:
          return date;
      }
    },
    timeAgo(datetime) {
      const targetDatetime = dayjs(datetime);
      const seconds = dayjs().diff(targetDatetime, 'second');

      if (seconds >= 0) {
        for (const { to, template } of timeAgoRanges) {
          if (seconds < to) {
            return template(seconds);
          }
        }
      }

      const isCurrentYear = targetDatetime.year() === dayjs().year();

      return isCurrentYear ? this.dateTime(datetime, { shortMonth: true }) : this.date(datetime, { shortMonth: true });
    },
  };
};
